import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { AssetVizMode } from '@asset-viz/defines'
import React from 'react'
import { useAssetVizTheme } from '@asset-viz/providers/theme'
import { Color } from 'three'
import { GeckoMarkupLayer, GeckoMarkupPrimitive } from 'types'

/**
 * A hook used to standardize the behavior of markup primitives within AssetViz
 * @param props     The primitive in question and the markup layer it belongs to
 */
export const useMarkupLayerProperties = (props: {
  markupLayer: GeckoMarkupLayer
  primitive: GeckoMarkupPrimitive | undefined
}) => {
  const { selection, draftSelection, mode } = useAssetVizInternal()
  const theme = useAssetVizTheme()
  return React.useMemo(() => {
    const isEditing =
      (selection instanceof GeckoMarkupLayer ||
        selection instanceof GeckoMarkupPrimitive) &&
      mode !== AssetVizMode.SelectMarkupRect
    const isEditingThisPrimitive =
      selection instanceof GeckoMarkupPrimitive &&
      selection.id === props.primitive?.id &&
      selection.markupLayerID === props.markupLayer.id &&
      mode !== AssetVizMode.SelectMarkupRect
    const isEditingThisLayer =
      (selection instanceof GeckoMarkupLayer &&
        selection.id === props.markupLayer.id) ||
      (selection instanceof GeckoMarkupPrimitive &&
        selection.markupLayerID === props.markupLayer.id &&
        mode !== AssetVizMode.SelectMarkupRect)
    const isHoveringThisPrimitive =
      draftSelection instanceof GeckoMarkupPrimitive &&
      draftSelection.id === props.primitive?.id &&
      draftSelection.markupLayerID === props.markupLayer.id
    const isSelectingTheseLayers = mode === AssetVizMode.SelectMarkupRect
    const isSelectingThisPrimitive =
      selection instanceof GeckoMarkupPrimitive &&
      selection.id === props.primitive?.id &&
      selection.markupLayerID === props.markupLayer.id &&
      mode === AssetVizMode.SelectMarkupRect

    let { r, g, b } = new Color(
      props.markupLayer.data.color
    ).convertLinearToSRGB()

    // Transforms per https://www.w3.org/TR/WCAG21/#contrast-minimum
    if (r <= 0.04045) r = r / 12.92
    else r = Math.pow((r + 0.055) / 1.055, 2.4)
    if (g <= 0.04045) g = g / 12.92
    else g = Math.pow((g + 0.055) / 1.055, 2.4)
    if (b <= 0.04045) b = b / 12.92
    else b = Math.pow((b + 0.055) / 1.055, 2.4)

    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
    // Based on whether contrast is higher with black or white
    const colorIsBright = luma > 0.1791

    return {
      color: props.markupLayer.data.color ?? theme.icon1,
      outlineColor: colorIsBright ? 'black' : 'white', // Shouldn't match theme
      opacity: !isEditing || isEditingThisLayer ? 1 : 0.1,
      isEditingThisLayer,
      isSelected: isEditingThisPrimitive,
      isHovered: isHoveringThisPrimitive,
      isSelectedForAnalysis: isSelectingThisPrimitive,
      isSelectable: isSelectingTheseLayers,
    }
  }, [props, selection, draftSelection, mode, theme])
}
